import { ReactElement } from "react";
import LayoutCard from "../shared-ui-components/LayoutCard";
import "./ObjectTreeStruct.scss";
import { BarChartOutlined } from "@ant-design/icons";
import {
  ServerAnalysisItem,
  ServerCategorizationsData,
} from "../models/data-structures";
import TreeNode from "./TreeNode";

function hashStringToId(str: string): number {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash); // Ensure positive integer
}

function flattenSingleChildFolders(
  node: ServerAnalysisItem
): ServerAnalysisItem {
  if (node.ObjectType === "Folder") {
    while (node.children && node.children.length === 1) {
      const child = node.children[0];
      node.Name = `${node.Name}/${child.Name}`;
      node.ObjectType = child.ObjectType;
      node.ObjectSize = child.ObjectSize;
      node.IndicatorCategories = child.IndicatorCategories;
      node.MetaData = child.MetaData;
      node.Id = child.Id;
      node.ParentId = child.ParentId;
      node.Rating = child.Rating;
      node.Sha256 = child.Sha256;
      node.numberOfCopies = child.numberOfCopies;
      node.children = child.children;
    }
  }

  if (node.children) {
    node.children = node.children.map(flattenSingleChildFolders);
  }

  return node;
}

function buildTree(data: ServerAnalysisItem[]): ServerAnalysisItem[] {
  const map = new Map<number, ServerAnalysisItem>();
  const roots: ServerAnalysisItem[] = [];

  // Populate the map with initial items and handle duplicates
  data.forEach((item) => {
    let existingItem = Array.from(map.values()).find(
      (existing) =>
        existing.Name === item.Name &&
        existing.ObjectType === item.ObjectType &&
        existing.ObjectSize === item.ObjectSize &&
        existing.ParentId === item.ParentId &&
        existing.Rating === item.Rating
    );

    if (existingItem) {
      // If it's a duplicate, increment the count
      existingItem.numberOfCopies = (existingItem.numberOfCopies || 1) + 1;
    } else {
      // If not a duplicate, add to the map, ensuring all properties are copied
      map.set(item.Id, { ...item, children: [] });
    }
  });

  // Function to get the new filename without -> path
  function getFilename(item: ServerAnalysisItem): string {
    const pathParts = item.Name.split("->");
    return pathParts[pathParts.length - 1];
  }

  // Update the items with new names
  map.forEach((item) => {
    const newItem = map.get(item.Id);
    if (newItem) {
      newItem.Name = getFilename(newItem);
    }
  });

  //Const with all the object types that can have \ in their names
  const fileTypesWithBackslashesInNames: string[] = [
    "AIF",
    "BAT",
    "BMP",
    "GIF",
    "INI",
    "INF",
    "JPG",
    "PDF",
    "PNG",
    "RTF",
    "SH",
    "SWF",
    "SCRIPT",
    "TEXT",
    "TIFF",
    "WMF",
    "XML",
  ];

  // Function to add nested items based on path
  function addNestedItems(
    parent: ServerAnalysisItem,
    pathParts: string[],
    originalItem: ServerAnalysisItem
  ) {
    if (pathParts.length === 0) return;

    const part = pathParts.shift()!;
    let child = parent.children!.find((child) => child.Name === part);
    const isLastPart = pathParts.length === 0;

    if (!child) {
      child = {
        Name: part,
        ObjectType: isLastPart ? originalItem.ObjectType : "Folder",

        ObjectSize: isLastPart ? originalItem.ObjectSize : 0,
        IndicatorCategories: isLastPart ? originalItem.IndicatorCategories : [],
        MetaData: isLastPart ? originalItem.MetaData : undefined,
        Id: isLastPart ? originalItem.Id : hashStringToId(part),
        ParentId: parent.Id,
        Rating: isLastPart ? originalItem.Rating : 0,
        Sha256: isLastPart ? originalItem.Sha256 : "",
        children: isLastPart ? originalItem.children : [],
        numberOfCopies: isLastPart ? originalItem.numberOfCopies : 0,
      };
      parent.children!.push(child);
    } else if (child.Name === part && isLastPart) {
      // Edge case: When the object name is the same as the parents
      child = {
        Name: part,
        ObjectType: originalItem.ObjectType,
        ObjectSize: originalItem.ObjectSize,
        IndicatorCategories: originalItem.IndicatorCategories,
        MetaData: originalItem.MetaData,
        Id: originalItem.Id,
        ParentId: parent.Id,
        Rating: originalItem.Rating,
        children: originalItem.children,
        Sha256: originalItem.Sha256,
        numberOfCopies: originalItem.numberOfCopies,
      };
      parent.children!.push(child);
    }
    addNestedItems(child, pathParts, originalItem);
  }

  // Assign children to their respective parents and handle nested paths
  map.forEach((item) => {
    if (item.Name.includes("/")) {
      // Handle nested paths
      const pathParts = item.Name.split("/");
      const parent = map.get(item.ParentId);
      if (parent) {
        addNestedItems(parent, pathParts, item);
      }
    } else if (item.Name.includes("\\")) {
      if (!fileTypesWithBackslashesInNames.includes(item.ObjectType)) {
        const pathParts = item.Name.split("\\");
        const parent = map.get(item.ParentId);
        if (parent) {
          addNestedItems(parent, pathParts, item);
        }
      }
    } else {
      // Assign direct children to their parents
      if (item.ParentId !== item.Id) {
        // Ensure it's not a root item
        const parent = map.get(item.ParentId);
        if (parent) {
          parent.children!.push(item);
        }
      } else {
        // It's a root item
        roots.push(item);
      }
    }
  });

  // Flatten single-child folders in the entire tree
  const flattenedRoots = roots.map(flattenSingleChildFolders);

  return flattenedRoots;
}

export default function ObjectTreeStruct({
  data,
}: {
  data: ServerCategorizationsData;
}): ReactElement {
  const fileAnalysisData = data;
  //console.log(fileAnalysisData);

  const objectsArray = Array.isArray(fileAnalysisData.Objects) ? fileAnalysisData.Objects : [];
  const tree = buildTree(objectsArray);
  //console.log(tree);

  return (
    <LayoutCard className="main">
      {{
        header: (
          <div className="layout-card-header-text flex-row-with-vertical-alignment">
            <BarChartOutlined className="icon-with-background" />
            Object Overview
          </div>
        ),
        content: (
          <div>
            {tree.length === 0 ? (
              <div className="object-tree-empty">No object data available.</div>
            ) : (
              tree.map((rootNode, index) => (
                <TreeNode key={index} name={rootNode.Name} node={rootNode} />
              ))
            )}
          </div>
        ),
      }}
    </LayoutCard>
  );
}
